// Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";
import { login } from "../../store/authSlice";
import { Button } from "../../components/js-components/button";
import axiosInstance from "../../utils/axiosConfig";
import { showLocationDialog } from "../../utils/locationUtils";

function Login() {
  const [formData, setFormData] = useState({
    identifier: '', // Can be username, email, or name
    password: ''
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      console.log("Login Attempt:", { 
        identifier: formData.identifier, 
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL 
      });

      const response = await axiosInstance.post('/auth/login', formData);

      console.log("Full Login Response:", response.data);

      if (response.status === 403) {
        console.log("User not authorized:", response.data);
        setError(response.data.message || "Not authorized. Wait for an admin to approve your account.");
        setLoading(false);
        return;
      }

      if (!response.data.token) {
        console.error("No token in response:", response.data);
        setError("Login failed. Please try again.");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", response.data.token);

      dispatch(
        login({
          id: response.data.user.id,
          username: response.data.user.username,
          name: response.data.user.name,
          role: response.data.user.role,
          locationid: response.data.user.locationid,
          isAuthorized: response.data.isAuthorized
        })
      );

      // Request location permission after successful login
      try {
        console.log("🔍 Requesting location permission for user:", response.data.user.id);
        const locationResult = await showLocationDialog(response.data.user.id);
        
        if (locationResult.success) {
          console.log("✅ Location permission granted and data stored successfully");
          console.log("📍 Location data:", locationResult.data);
        } else {
          console.log("⚠️ Location permission not granted:", locationResult.message);
        }
      } catch (locationError) {
        console.log("❌ Location permission error:", locationError.message);
        // Don't block login if location permission is denied
      }

      console.log("Navigating to Dashboard");
      navigate("/dashboard");
    } catch (err) {
      console.error("Login Error:", {
        error: err,
        response: err.response?.data,
        message: err.message,
        config: err.config,
        code: err.code
      });

      setError(
        err.response?.data?.message ||
        "Login failed. Please check your credentials and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container flex justify-center items-center min-h-screen bg-gray-100">
      <div className="login-card w-full max-w-md bg-white shadow-lg rounded-xl p-8">
        {error && (
          <div 
            className="unauthorized-banner flex items-center bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-r-lg shadow-md" 
            role="alert"
          >
            <AlertTriangle className="mr-4 text-yellow-500" size={24} />
            <span className="font-semibold text-sm">{error}</span>
          </div>
        )}
        
        <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Log in to Your Account</h1>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label 
              htmlFor="identifier" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Username, Email, or Name
            </label>
            <input
              type="text"
              id="identifier"
              value={formData.identifier}
              onChange={handleChange}
              placeholder="Enter your username, email, or name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="mb-2">
            <label 
              htmlFor="password" 
              className="block text-gray-700 text-sm font-bold mb-2"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              />
              <button
                type="button"
                tabIndex={-1}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 focus:outline-none"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="text-right mb-4">
            <a 
              href="/forgot-password" 
              className="text-sm text-blue-500 hover:text-blue-700"
            >
              Forgot Password?
            </a>
          </div>
          
          <Button 
            type="submit" 
            disabled={loading} 
            className="w-full bg-red-500 text-white py-2 rounded-md hover:bg-red-600 transition duration-300 ease-in-out"
          >
            {loading ? "Logging in..." : "Log In"}
          </Button>
        </form>

        <p className="text-center mt-4 text-sm text-gray-600">
          Don't have an account? {" "}
          <a 
            href="/signup" 
            className="text-blue-500 hover:text-blue-700 font-semibold"
          >
            Create an account
          </a>
        </p>
      </div>
    </div>
  );
}

export default Login;
